import { useState } from 'react';
import { api } from '../api';
import { haptic } from '../telegram';
import type { CareTaskDto } from '../types';
import { Card, formatDate } from './common';

interface Props {
  task: CareTaskDto;
  onChanged: () => void;
  onOpenPlant?: (plantId: string) => void;
}

function timeOf(value: string) {
  return new Date(value).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/** Что уже сделано с задачей — для секции «Готово». */
function doneText(task: CareTaskDto): string {
  if (task.status === 'completed') {
    const what =
      task.doneLabel ?? (task.type === 'watering_check' ? 'Полито' : 'Сделано');
    return `✅ ${what}${task.completedAt ? ` в ${timeOf(task.completedAt)}` : ''}`;
  }
  if (task.status === 'snoozed' && task.snoozedUntil) {
    const when = new Date(task.snoozedUntil).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
    return task.type === 'watering_check'
      ? `🌧 Ещё влажно — проверим ${when}`
      : `⏰ Перенесено на ${when}`;
  }
  return '✅ Отмечено';
}

export function DoneTaskCard({ task, onOpenPlant }: Pick<Props, 'task' | 'onOpenPlant'>) {
  return (
    <Card className="done">
      <div className="row between">
        <div className="grow">
          <div className="title">{task.plantName}</div>
          <div className="hint">{task.title}</div>
        </div>
        {onOpenPlant ? (
          <button className="btn secondary small" onClick={() => onOpenPlant(task.plantId)}>
            Открыть
          </button>
        ) : null}
      </div>
      <div className="mt">{doneText(task)}</div>
    </Card>
  );
}

function TreatmentDetails({ details }: { details: NonNullable<CareTaskDto['details']> }) {
  const rows: [string, string | undefined][] = [
    ['⏰ Когда', details.timing],
    ['🥣 Как развести', details.preparation || details.dosage],
    ['💦 Как обработать', details.application],
    ['⚠️ Осторожно', details.precautions],
  ];
  return (
    <div className="mt">
      <div>
        🧴 <b>{details.product}</b>
        {details.active_ingredient ? <span className="hint"> ({details.active_ingredient})</span> : null}
        {details.course_total && details.course_total > 1 ? (
          <span className="badge" style={{ marginLeft: 6 }}>
            {details.course_index} из {details.course_total}
          </span>
        ) : null}
      </div>
      {details.target ? <div className="hint">От чего: {details.target}</div> : null}
      {rows
        .filter(([, value]) => value)
        .map(([label, value]) => (
          <div key={label} className="mt">
            <b>{label}:</b> {value}
          </div>
        ))}
      <div className="hint mt">Сверьтесь с инструкцией на упаковке.</div>
    </div>
  );
}

export function TaskCard({ task, onChanged, onOpenPlant }: Props) {
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState(false);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
      haptic('success');
      onChanged();
    } catch {
      haptic('error');
    } finally {
      setBusy(false);
    }
  }

  const isWatering = task.type === 'watering_check';

  return (
    <Card className={task.overdue ? 'overdue' : 'today'}>
      <div className="row between">
        <div className="grow">
          <div className="title">{task.plantName}</div>
          <div className="hint">{task.title}</div>
        </div>
        <span className="badge">{formatDate(task.scheduledAt, true)}</span>
      </div>

      {task.details?.kind === 'treatment' ? (
        <TreatmentDetails details={task.details} />
      ) : task.instruction ? (
        <div className="mt">{task.instruction}</div>
      ) : null}

      {why && task.reasonSummary ? <div className="hint mt">{task.reasonSummary}</div> : null}

      <div className="actions">
        <button className="btn" disabled={busy} onClick={() => run(() => api.completeTask(task.id))}>
          {task.doneLabel ?? (isWatering ? '💧 Полил' : '✅ Сделал')}
        </button>
        {isWatering ? (
          <button
            className="btn secondary"
            disabled={busy}
            onClick={() => run(() => api.soilStillWet(task.id))}
          >
            🌧 Ещё влажно
          </button>
        ) : null}
        <button
          className="btn secondary"
          disabled={busy}
          onClick={() => run(() => api.snoozeTask(task.id, 24))}
        >
          ⏰ Завтра
        </button>
        {task.reasonSummary ? (
          <button className="btn secondary" onClick={() => setWhy((v) => !v)}>
            ❓ Почему
          </button>
        ) : null}
        {onOpenPlant ? (
          <button className="btn secondary" onClick={() => onOpenPlant(task.plantId)}>
            Открыть
          </button>
        ) : null}
      </div>
    </Card>
  );
}
